import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  IBillingUrlFormResponse,
  IHealthCare,
  IHealthCareDoctorForm,
  IHealthCareFilter,
  IHealthCareSchemeForm,
  IHealthCareRequiredDocument,
  IManageUserForm,
  ISchemeSpecificLoginDetailsForm,
  IHealthCareScheme,
  ISchemeSpecificLoginResetPasswordForm,
} from "../interfaces/IHealthCare";
import {
  fetchHealthCares,
  postBillingUrlForm,
  postHealthCare,
  postHealthCareDoctor,
  postHealthCareRequiredDocument,
  postHealthCareScheme,
  postManageUser,
  postSchemeSpecificLoginDetails,
} from "../apis/healthCareService/postHealthCareService";
import {
  fetchAllBillingUrlForm,
  fetchAllDepartments,
  fetchAllDesignation,
  fetchAllHealthCareDoctor,
  fetchAllHealthCareScheme,
  fetchAllManageUsers,
  fetchAllPortals,
  fetchAllQualification,
  fetchAllSchemeSpecificLoginDetails,
  fetchHealthCareById,
} from "../apis/healthCareService/getHealthCareService";
import { fetchAllSchemes } from "../apis/schemeService";
import {
  deleteBillingUrlForm,
  deleteHealthCareDoctor,
  deleteHealthCareRequiredDocument,
  deleteManageUser,
  deleteSchemeSpecificLoginDetail,
} from "../apis/healthCareService/deleteHealthCareService";
import {
  modifyBillingUrlForm,
  modifyHealthCareDetails,
  modifyHealthCareDoctor,
  modifyHealthCareRequiredDocument,
  modifyHealthCareScheme,
  modifyManageUser,
  modifySchemeSpecificLoginDetails,
  resetHealthCareUserPassword
} from "../apis/healthCareService/patchHealthCareService";

export const getHealthCares = createAsyncThunk(
  "healthCares/getHealthCares",
  async ({ filters }: { filters: IHealthCareFilter }) => {
    filters = { ...filters, pageNumber: filters.pageNumber - 1 };
    const response = await fetchHealthCares(filters);
    return response;
  }
);

export const postSubmitHealthCare = createAsyncThunk(
  "healthCares/postSubmitHealthCare",
  async ({ formData }: { formData: IHealthCare }) => {
    const response = await postHealthCare(formData);
    return response;
  }
);

export const getHealthCareById = createAsyncThunk(
  "healthCares/getHealthCareById",
  async ({ id }: { id: number }) => {
    const response = await fetchHealthCareById(id);
    return response;
  }
);

export const postSubmitHealthCareScheme = createAsyncThunk(
  "healthCares/postSubmitHealthCareScheme",
  async ({
    healthCareId,
    schemeId,
    formData,
  }: {
    healthCareId: number;
    schemeId: number;
    formData: IHealthCareScheme;
  }) => {
    const response = await postHealthCareScheme(
      healthCareId,
      schemeId,
      formData
    );
    return response;
  }
);

export const getAllHealthCareScheme = createAsyncThunk(
  "healthCares/getHealthCareScheme",
  async ({ healthCareId }: { healthCareId: number }) => {
    const response = await fetchAllHealthCareScheme(healthCareId);
    return response;
  }
);

export const postSubmitHealthCareDoctor = createAsyncThunk(
  "healthCares/postSubmitHealthCareDoctor",
  async ({
    healthCareId,
    formData,
  }: {
    healthCareId: number;
    formData: IHealthCareDoctorForm;
  }) => {
    const response = await postHealthCareDoctor(healthCareId, formData);
    return response;
  }
);

export const getAllHealthCareDoctor = createAsyncThunk(
  "healthCares/getAllHealthCareDoctor",
  async ({ healthCareId }: { healthCareId: number }) => {
    const response = await fetchAllHealthCareDoctor(healthCareId);
    return response;
  }
);

export const deleteHealthCareDoctorById = createAsyncThunk(
  "healthCares/deleteHealthCareDoctorById",
  async ({ healthCareId, id }: { healthCareId: number; id: number }) => {
    const response = await deleteHealthCareDoctor(healthCareId, id);
    return response;
  }
);

export const postSubmitHealthCareUser = createAsyncThunk(
  "healthCares/postSubmitHealthCareUser",
  async ({
    healthCareId,
    formData,
  }: {
    healthCareId: number;
    formData: IManageUserForm;
  }) => {
    const response = await postManageUser(healthCareId, formData);
    return response;
  }
);

export const getAllManageUsers = createAsyncThunk(
  "healthCares/getAllManageUsers",
  async ({ healthCareId }: { healthCareId: number }) => {
    const response = await fetchAllManageUsers(healthCareId);
    return response;
  }
);

export const deleteHealthCareUserById = createAsyncThunk(
  "healthCares/deleteHealthCareUserById",
  async ({ healthCareId, id }: { healthCareId: number; id: number }) => {
    const response = await deleteManageUser(healthCareId, id);
    return response;
  }
);

export const postSubmitSchemeSpecificLoginDetails = createAsyncThunk(
  "healthCares/postSubmitSchemeSpecificLoginDetails",
  async ({
    healthCareId,
    formData,
  }: {
    healthCareId: number;
    formData: ISchemeSpecificLoginDetailsForm;
  }) => {
    const response = await postSchemeSpecificLoginDetails(
      healthCareId,
      formData
    );
    return response;
  }
);

export const getAllBillingUrlForm = createAsyncThunk(
  "healthCares/getAllBillingUrlForm",
  async ({ healthCareId }: { healthCareId: number }) => {
    const response = await fetchAllBillingUrlForm(healthCareId);
    return response;
  }
);

export const deleteBillingUrlById = createAsyncThunk(
  "healthCares/deleteBillingUrlById",
  async ({ healthCareId, id }: { healthCareId: number; id: number }) => {
    const response = await deleteBillingUrlForm(healthCareId, id);
    return response;
  }
);

export const postSubmitBillingUrlForm = createAsyncThunk(
  "healthCares/postSubmitBillingUrlForm",
  async ({
    healthCareId,
    formData,
  }: {
    healthCareId: number;
    formData: IBillingUrlFormResponse;
  }) => {
    const response = await postBillingUrlForm(healthCareId, formData);
    return response;
  }
);

export const getAllSchemeSpecificLoginDetails = createAsyncThunk(
  "healthCares/getAllSchemeSpecificLoginDetails",
  async ({ healthCareId }: { healthCareId: number }) => {
    const response = await fetchAllSchemeSpecificLoginDetails(healthCareId);
    return response;
  }
);

export const deleteSchemeSpecificLoginDetailById = createAsyncThunk(
  "healthCares/deleteSchemeSpecificLoginDetailById",
  async ({ healthCareId, id }: { healthCareId: number; id: number }) => {
    const response = await deleteSchemeSpecificLoginDetail(healthCareId, id);
    return response;
  }
);

export const postSubmitHealthCareRequiredDocument = createAsyncThunk(
  "healthCares/postSubmitHealthCareRequiredDocument",
  async ({
    healthCareId,
    formData,
  }: {
    healthCareId: number;
    formData: IHealthCareRequiredDocument;
  }) => {
    const response = await postHealthCareRequiredDocument(
      healthCareId,
      formData
    );
    return response;
  }
);

export const deleteHealthCareRequiredDocumentById = createAsyncThunk(
  "healthCares/deleteHealthCareRequiredDocumentById",
  async ({ healthCareId, id }: { healthCareId: number; id: number }) => {
    const response = await deleteHealthCareRequiredDocument(healthCareId, id);
    return response;
  }
);

export const getAllPortals = createAsyncThunk(
  "healthCares/getAllPortals",
  async () => {
    const response = await fetchAllPortals();
    return response;
  }
);

export const getAllDepartments = createAsyncThunk(
  "healthCares/getAllDepartments",
  async () => {
    const response = await fetchAllDepartments();
    return response;
  }
);

export const getAllSchemes = createAsyncThunk(
  "healthCares/getAllSchemes",
  async () => {
    const response = await fetchAllSchemes();
    return response;
  }
);

export const getAllDesignations = createAsyncThunk(
  "healthCares/getAllDesignations",
  async () => {
    const response = await fetchAllDesignation();
    return response;
  }
);

export const getAllQualifications = createAsyncThunk(
  "healthCares/getAllQualifications",
  async () => {
    const response = await fetchAllQualification();
    return response;
  }
);

export const updateHealthCareById = createAsyncThunk(
  "healthCares/updateHealthCareById",
  async ({ id, formData }: { id: number; formData: IHealthCare }) => {
    const response = await modifyHealthCareDetails(id, formData);
    return response;
  }
);

export const updateHealthCareSchemeById = createAsyncThunk(
  "healthCares/updateHealthCareSchemeById",
  async ({
    healthCareId,
    id,
    schemeId,
    formData,
  }: {
    healthCareId: number;
    id: number;
    schemeId: number;
    formData: IHealthCareScheme;
  }) => {
    const response = await modifyHealthCareScheme(
      healthCareId,
      id,
      schemeId,
      formData
    );
    return response;
  }
);

export const updateHealthCareDoctorById = createAsyncThunk(
  "healthCares/updateHealthCareDoctorById",
  async ({
    healthCareId,
    id,
    formData,
  }: {
    healthCareId: number;
    id: number;
    formData: IHealthCareDoctorForm;
  }) => {
    const response = await modifyHealthCareDoctor(healthCareId, id, formData);
    return response;
  }
);

export const updateHealthCareUserById = createAsyncThunk(
  "healthCares/updateHealthCareUserById",
  async ({
    healthCareId,
    id,
    formData,
  }: {
    healthCareId: number;
    id: number;
    formData: IManageUserForm;
  }) => {
    const response = await modifyManageUser(healthCareId, id, formData);
    return response;
  }
);

export const updateHealthCareSchemeSpecificLoginDetailById = createAsyncThunk(
  "healthCares/updateHealthCareSchemeSpecificLoginDetailById",
  async ({
    healthCareId,
    id,
    formData,
  }: {
    healthCareId: number;
    id: number;
    formData: ISchemeSpecificLoginDetailsForm;
  }) => {
    const response = await modifySchemeSpecificLoginDetails(
      healthCareId,
      id,
      formData
    );
    return response;
  }
);

export const resetHealthCareUserPasswordById = createAsyncThunk(
  "healthCares/resetHealthCareUserPasswordById",
  async (formData: ISchemeSpecificLoginResetPasswordForm) => {
    const response = await resetHealthCareUserPassword(formData);
    return response;
  }
);

export const updateHealthCareBillingUrlById = createAsyncThunk(
  "healthCares/updateHealthCareBillingUrlById",
  async ({
    healthCareId,
    id,
    formData,
  }: {
    healthCareId: number;
    id: number;
    formData: IBillingUrlFormResponse;
  }) => {
    const response = await modifyBillingUrlForm(healthCareId, id, formData);
    return response;
  }
);

export const updateHealthCareRequiredDocumentById = createAsyncThunk(
  "healthCares/updateHealthCareRequiredDocumentById",
  async ({
    healthCareId,
    formData,
  }: {
    healthCareId: number;
    formData: IHealthCareRequiredDocument;
  }) => {
    const response = await modifyHealthCareRequiredDocument(
      healthCareId,
      formData
    );
    return response;
  }
);
